import { Building2, MapPin, BadgeCheck, ListChecks, GraduationCap } from "lucide-react";

function Section({ icon: Icon, title, accent, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Icon className={accent ? "h-4 w-4" : "h-4 w-4 text-primary"} style={accent ? { color: accent } : undefined} />
        <span>{title}</span>
      </div>
      {children}
    </div>
  );
}

const Bullet = ({ children, accent }) => (
  <li className="flex gap-2.5 text-sm">
    <span className={accent ? "mt-1" : "mt-1 text-primary"} style={accent ? { color: accent } : undefined}>▸</span>
    <span>{children}</span>
  </li>
);

/**
 * Canonical job presentation reused across recruiter builder, public careers,
 * branded org careers and the candidate application overview.
 * variant: "recruiter" shows scoring competencies; "public"/"candidate" hide them.
 * accent: optional brand colour (org careers) applied to icons/bullets.
 */
export function JobDetail({ job, variant = "public", accent }) {
  if (!job) return null;
  const showComp = variant === "recruiter" && (job.competencies?.length > 0);
  const iconStyle = accent ? { color: accent } : undefined;
  const bulletText = (r) => (typeof r === "string" ? r : (r?.text || r?.name || ""));

  return (
    <div className="space-y-6" data-testid="job-detail">
      <div className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight" data-testid="job-detail-title">{job.title}</h1>
        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
          {job.department && <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" style={iconStyle} /> {job.department}</span>}
          {job.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" style={iconStyle} /> {job.location}</span>}
          {(job.seniority || job.employment_type) && (
            <span className="flex items-center gap-1.5"><BadgeCheck className="h-4 w-4" style={iconStyle} /> {[job.seniority, job.employment_type].filter(Boolean).join(" · ")}</span>
          )}
          {variant !== "recruiter" && (
            <span className="flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border border-border" data-testid="job-detail-provenance">
              {job.provenance === "manual" ? "Human-written role" : job.provenance === "ai" ? "AI-assisted draft · human-reviewed" : "Authorship unverified"}
            </span>
          )}
        </div>
      </div>

      {job.summary && <p className="text-base leading-relaxed text-foreground/90" data-testid="job-detail-summary">{job.summary}</p>}

      {job.responsibilities?.length > 0 && (
        <Section icon={ListChecks} title="What you'll do" accent={accent}>
          <ul className="space-y-2" data-testid="job-detail-responsibilities">
            {job.responsibilities.map((r, i) => <Bullet key={i} accent={accent}>{bulletText(r)}</Bullet>)}
          </ul>
        </Section>
      )}

      {job.requirements?.length > 0 && (
        <Section icon={GraduationCap} title="What we're looking for" accent={accent}>
          <ul className="space-y-2" data-testid="job-detail-requirements">
            {job.requirements.map((r, i) => <Bullet key={i} accent={accent}>{bulletText(r)}</Bullet>)}
          </ul>
        </Section>
      )}

      {showComp && (
        <Section icon={BadgeCheck} title="How we'll assess" accent={accent}>
          <p className="text-xs text-muted-foreground mb-3">These weighted competencies drive explainable candidate scoring. Weights sum to 100%.</p>
          <div className="space-y-4" data-testid="job-detail-competencies">
            {job.competencies.map((c, i) => (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{c.name}</span>
                  <span className="font-mono text-xs text-primary">{c.weight}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${c.weight}%` }} />
                </div>
                {c.description && <p className="text-xs text-muted-foreground">{c.description}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

export default JobDetail;
